/**
 * Tax identifiers on a certified invoice — the rule for each, in one place.
 *
 * A Portuguese NIF is checked by its check digit (mod 11), as the AT computes
 * it; an EU VAT number by its format here and, for the reverse charge, by VIES
 * on the server (the format is only the pre-check — VIES is the authority and
 * its answer is the proof kept). Any other country's identifier is free text:
 * the platform cannot know its rule, and the invoice carries it as given.
 *
 * Messages are i18n keys.
 */
import { z } from 'zod';

/** The 27 EU member states, ISO 3166-1 alpha-2. */
export const EU_MEMBER_STATES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
] as const;
export type EuMemberState = (typeof EU_MEMBER_STATES)[number];

export function isEuMemberState(country: string): country is EuMemberState {
  return (EU_MEMBER_STATES as readonly string[]).includes(country.toUpperCase());
}

/** The VAT prefix of a member state — Greece's is `EL`, every other its ISO code. */
export function vatPrefixOf(country: EuMemberState): string {
  return country === 'GR' ? 'EL' : country;
}

/** VIES's number formats, without the prefix. */
const EU_VAT_FORMATS: Record<EuMemberState, RegExp> = {
  AT: /^U\d{8}$/,
  BE: /^[01]\d{9}$/,
  BG: /^\d{9,10}$/,
  CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^\d{9}$/,
  DK: /^\d{8}$/,
  EE: /^\d{9}$/,
  ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^\d{8}$/,
  FR: /^[A-HJ-NP-Z0-9]{2}\d{9}$/,
  GR: /^\d{9}$/,
  HR: /^\d{11}$/,
  HU: /^\d{8}$/,
  IE: /^(\d{7}[A-W][A-I]?|\d[A-Z+*]\d{5}[A-W])$/,
  IT: /^\d{11}$/,
  LT: /^(\d{9}|\d{12})$/,
  LU: /^\d{8}$/,
  LV: /^\d{11}$/,
  MT: /^\d{8}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^\d{2,10}$/,
  SE: /^\d{12}$/,
  SI: /^\d{8}$/,
  SK: /^\d{10}$/,
};

/**
 * The identifier as the documents carry it: no spaces, dots or dashes, upper
 * case, and — for an EU member state — without its own VAT prefix.
 */
export function normalizeTaxId(country: string, raw: string): string {
  const compact = raw.replace(/[\s.\-/]/g, '').toUpperCase();
  const code = country.toUpperCase();
  if (!isEuMemberState(code)) return compact;
  const prefix = vatPrefixOf(code);
  return compact.startsWith(prefix) ? compact.slice(prefix.length) : compact;
}

/**
 * A Portuguese NIF: nine digits, a first digit (or pair) the AT issues, and the
 * mod-11 check digit.
 */
export function isValidPortugueseNif(nif: string): boolean {
  if (!/^\d{9}$/.test(nif)) return false;
  if (!/^([1235689]|45|7[0-9]|9[0189])/.test(nif)) return false;
  const digits = nif.split('').map(Number);
  const sum = digits.slice(0, 8).reduce((acc, digit, i) => acc + digit * (9 - i), 0);
  const remainder = sum % 11;
  const check = remainder < 2 ? 0 : 11 - remainder;
  return check === digits[8];
}

/** The identifier is well formed for its country (normalized first). */
export function isWellFormedTaxId(country: string, raw: string): boolean {
  const code = country.toUpperCase();
  const taxId = normalizeTaxId(code, raw);
  if (code === 'PT') return isValidPortugueseNif(taxId);
  if (isEuMemberState(code)) return EU_VAT_FORMATS[code].test(taxId);
  return /^[A-Z0-9]{2,30}$/.test(taxId);
}

/** The tax identifier field — its country checks it (see `billingProfileSchema`). */
export const taxIdSchema = z.string().trim().max(40, 'billing.profile.validation.taxIdInvalid');
